// ═══════════════════════════════════════
// ATLAS BEASTS — CORE TYPES
// Ploonu LLC — Confidential
// ═══════════════════════════════════════

// ── Elements ──
export type Element = 'fire' | 'water' | 'earth' | 'air' | 'void' | 'aether'

// ── Zones ──
export type ZoneId =
  | 'ashwake'
  | 'rootlands'
  | 'glassdeep'
  | 'skyrift'
  | 'null-current'
  | 'convergence'

export interface Zone {
  id: ZoneId
  name: string
  element: Element
  description: string
  unlockLevel: number
  spawnMultiplier: number
  colorPrimary: string
  colorAccent: string
}

// ── Factions ──
export type FactionId =
  | 'drifters'
  | 'iron-meridian'
  | 'tidecallers'
  | 'wrecked'
  | 'sovereign-few'

export interface Faction {
  id: FactionId
  name: string
  description: string
  moralAlignment: string
  color: string
}

// ── Character Classes ──
export type CharacterClass =
  | 'currentblade'
  | 'tidecaller'
  | 'brawler'
  | 'bladestorm'
  | 'commander'
  | 'mystic'
  | 'scout'
  | 'berserker'
  | 'assassin'

// ── Awakening Stages ──
export type AwakeningStage = 1 | 2 | 3

// ── Characters ──
export interface CharacterStats {
  attack: number
  defense: number
  speed: number
  special: number
}

export interface CharacterMove {
  name: string
  type: 'melee' | 'ranged' | 'special' | 'charge' | 'control' | 'burst'
  element: Element
  power: number
  description: string
}

export interface Character {
  id: string
  name: string
  title: string
  faction: FactionId
  class: CharacterClass
  element: Element
  zone: ZoneId
  rarity: 'launch' | 'unlockable' | 'premium' | 'seasonal' | 'warden' | 'broken' | 'founder'
  stats: CharacterStats
  moves: CharacterMove[]
  awakeningStage: AwakeningStage
  bondedCreatureId: string | null
  backstory: string
  quote: string
  artUrl?: string
  spriteUrl?: string
}

// ── Creature Rarity ──
export type CreatureRarity = 'drift' | 'surge' | 'rift' | 'apex' | 'mythic'

// ── Evolution Stage ──
export type EvolutionStage = 1 | 2 | 3

// ── Creatures ──
export interface CreatureStats {
  attack: number
  defense: number
  speed: number
  bond: number
}

export interface CreatureMove {
  name: string
  type: string
  element: Element
  power: number
}

export interface Creature {
  id: string
  name: string
  species: string
  element: Element
  zone: ZoneId
  rarity: CreatureRarity
  evolutionStage: EvolutionStage
  stats: CreatureStats
  moves: CreatureMove[]
  bondedCharacterId: string | null
  bondLevel: number // 0-100
  condition: number // 0-100 — affects trade value
  isTrading: boolean
  isSoulbound: boolean // true at stage 3
  description: string
  artUrl?: string
  spriteUrl?: string
  isFoundingExclusive?: boolean
}

// ── Player ──
export interface Player {
  id: string
  username: string
  email: string
  level: number
  xp: number
  faction: FactionId | null
  zoneId: ZoneId
  atlasCoins: number
  shards: number
  streakDays: number
  lastActive: string
  isFoundingMember: boolean
  createdAt: string
}

// ── Player Inventory ──
export interface PlayerInventory {
  playerId: string
  characters: string[] // character IDs owned
  creatures: string[]  // creature IDs owned
  activeCrewIds: string[] // max 5 for PVP
  activeBonds: Record<string, string> // characterId -> creatureId
}

// ── Trade ──
export type TradeStatus = 'pending' | 'accepted' | 'rejected' | 'cancelled' | 'completed'

export interface Trade {
  id: string
  fromPlayerId: string
  toPlayerId: string
  offeredCreatureId: string
  requestedCreatureId: string
  status: TradeStatus
  createdAt: string
  expiresAt: string
}

// ── Bounty Board ──
export type BountyStatus = 'active' | 'captured' | 'expired'

export interface BountyEntry {
  id: string
  creatureId: string
  zoneId: ZoneId
  spawnedAt: string
  expiresAt: string
  status: BountyStatus
  requiredCharacterLevel: number
  bountyValue: number // atlas coins reward
}

// ── Battle ──
export type BattleType = 'encounter' | 'pvp' | 'raid' | 'warden'
export type BattleResult = 'victory' | 'defeat' | 'draw' | 'fled'

export interface Battle {
  id: string
  type: BattleType
  playerId: string
  opponentId?: string // null for PvE
  targetCreatureId?: string
  result: BattleResult
  xpGained: number
  coinsGained: number
  timestamp: string
}

// ── Raid ──
export interface Raid {
  id: string
  name: string
  targetId: string // warden or mythic creature
  startsAt: string
  endsAt: string
  minPlayers: number
  maxPlayers: number
  participantIds: string[]
  isActive: boolean
  isCompleted: boolean
  rewards: {
    creature?: string
    atlasCoins: number
    xp: number
  }
}

// ── Season ──
export interface Season {
  id: string
  name: string
  number: number
  startsAt: string
  endsAt: string
  themeElement: Element
  newCharacterIds: string[]
  newCreatureIds: string[]
  exclusiveRewards: string[]
}

// ── Auth ──
export interface AuthToken {
  playerId: string
  username: string
  email: string
  iat: number
  exp: number
}

export interface AuthState {
  player: Player | null
  token: string | null
  isLoading: boolean
  isAuthenticated: boolean
}

// ── API Response ──
export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

// ── Zone Config ──
export const ZONES: Record<ZoneId, Zone> = {
  'ashwake': {
    id: 'ashwake',
    name: 'The Ashwake',
    element: 'fire',
    description: 'A volcanic archipelago where light comes from below.',
    unlockLevel: 1,
    spawnMultiplier: 1,
    colorPrimary: '#FF4800',
    colorAccent: '#00F0FF',
  },
  'rootlands': {
    id: 'rootlands',
    name: 'The Rootlands',
    element: 'earth',
    description: 'Dense ancient forest where roots are wider than buildings.',
    unlockLevel: 1,
    spawnMultiplier: 1,
    colorPrimary: '#39FF14',
    colorAccent: '#00C8D7',
  },
  'glassdeep': {
    id: 'glassdeep',
    name: 'The Glassdeep',
    element: 'water',
    description: 'Infinite ocean depth with bioluminescent ruins below.',
    unlockLevel: 10,
    spawnMultiplier: 1.2,
    colorPrimary: '#00F0FF',
    colorAccent: '#80FFFF',
  },
  'skyrift': {
    id: 'skyrift',
    name: 'Skyrift',
    element: 'air',
    description: 'Cracked open sky with floating islands and wind bridges.',
    unlockLevel: 20,
    spawnMultiplier: 1.4,
    colorPrimary: '#B8EEFF',
    colorAccent: '#00F0FF',
  },
  'null-current': {
    id: 'null-current',
    name: 'The Null Current',
    element: 'void',
    description: 'Dead zone. No wind. No light. No rules.',
    unlockLevel: 35,
    spawnMultiplier: 1.8,
    colorPrimary: '#BF00FF',
    colorAccent: '#E8E8FF',
  },
  'convergence': {
    id: 'convergence',
    name: 'Convergence Zones',
    element: 'aether',
    description: 'Where elements meet. Where legends are made.',
    unlockLevel: 50,
    spawnMultiplier: 2.5,
    colorPrimary: '#00F0FF',
    colorAccent: '#80FFFF',
  },
}

// ── Element Config ──
export const ELEMENTS: Record<Element, { label: string; color: string; emoji: string }> = {
  fire:   { label: 'Fire',   color: '#FF4800', emoji: '🔥' },
  water:  { label: 'Water',  color: '#00F0FF', emoji: '💧' },
  earth:  { label: 'Earth',  color: '#39FF14', emoji: '🌿' },
  air:    { label: 'Air',    color: '#B8EEFF', emoji: '🌬️' },
  void:   { label: 'Void',   color: '#BF00FF', emoji: '🌑' },
  aether: { label: 'Aether', color: '#00F0FF', emoji: '✨' },
}

// ── Rarity Config ──
export const RARITY_CONFIG: Record<CreatureRarity, { label: string; color: string; weight: number }> = {
  drift:  { label: 'Drift',  color: '#5A7A8A', weight: 60 },
  surge:  { label: 'Surge',  color: '#39FF14', weight: 25 },
  rift:   { label: 'Rift',   color: '#00F0FF', weight: 10 },
  apex:   { label: 'Apex',   color: '#BF00FF', weight: 4  },
  mythic: { label: 'Mythic', color: '#00F0FF', weight: 1  },
}
